import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMount } from "react-use";

import {
  _,
  RIF,
  setSessionStorage,
  useCurrentWorkspaceAccountPlan,
  RequestResult,
  checkIsStaffUser,
  loadSessionStorage,
  useCollaboratorTypeInProject,
  collaboratorTypeHasRole,
  isInBetaType,
} from "../../lib";

import { DefaultProjectScheduleConfig, DexcomIntegration } from "../../model";

import {
  useSelectTheme,
  useCreateDispatchActions,
  useSelectAuth,
  useSelectProfile,
  useSelectWorkspaces,
  useSelectCollaborators,
  useSelectProjectData,
  useSelectFavoriteList,
  GlobalWorkspace,
  useSelectSettings,
  useSelectUnreadAnnouncement,
} from "../../store";

import {
  Button,
  Tooltip,
  ProjectCard,
  CollaboratorPage,
  Avatars,
  ButtonReverse,
  WorkspaceSettings,
  WorkspaceSidebar,
  PricingPage,
  PopupCreateProject,
  PopupNewFeature,
} from "..";

import {
  PlusIcon,
  CollaborateAddIcon,
  CheckSuccessIcon,
  WarningIcon,
  PlayButtonIcon,
  ThunderWhiteIcon,
  FavoriteEmptyState,
  SharedWithMeEmptyState,
  GettingStarted,
  LinkToBlueIcon,
  PlusBlueIcon,
  HeartRateWhiteIcon,
} from "../../asset/image";
import { useCurrentWorkspaceState } from "../../lib/custom_hooks/useCurrentWorkspaceState";
import {
  BetaType,
  IProject,
  IWorkspaceCollaborator,
  ProjectAuditTrailAction,
  ProjectCollaboratorRole,
  ProjectStatus,
  WorkspaceCollaboratorRole,
  WorkspaceCollaboratorType,
} from "../../shared/db";
import { DeepPartial } from "utility-types";

interface ProjectDeleteRequestResult extends RequestResult {
  projectId: string;
  prevProjectStatus: ProjectStatus;
}

export const ProjectLandingPage = () => {
  const { color, fontWeight, fontSize } = useSelectTheme();

  /* ------------------ req ------------------ */
  const {
    doREQUEST_DEXCOM_INTEGRATION_LIST_FETCH,
    doREQUEST_GLOBAL_WORKSPACE_LIST_FETCH,
    doREQUEST_PROJECT_EXTENSION_LIST_FETCH,
    doREQUEST_WORKSPACE_FAVORITE_CREATE,
    doREQUEST_WORKSPACE_FAVORITE_DELETE,
    doREQUEST_PROJECT_STATUS_UPDATE,
    doREQUEST_PROJECT_LIST_FETCH,
    doREQUEST_PROJECT_CREATE,
    doREQUEST_PROJECT_DUPLICATE,
    doREQUEST_PROJECT_AUDIT_TRAIL_CREATE,
    doREQUEST_WORKSPACE_COLLABORATOR_LIST_FETCH,
    doUNREAD_ANNOUNCEMENT_DISMISS,
  }: any = useCreateDispatchActions();

  const [requestResultOfProjectListFetch, setRequestResultOfProjectListFetch] =
    useState<RequestResult | null>(null);
  const [requestResultOfCollaboratorListFetch, setRequestResultOfCollaboratorListFetch] =
    useState<RequestResult | null>(null);
  const [requestResultOfGlobalWorkspaceListFetch, setRequestResultOfGlobalWorkspaceListFetch] =
    useState<RequestResult | null>(null);

  /* ------------------ reducer & store basic state ------------------ */
  const auth = useSelectAuth();
  const accessToken = auth?.accessToken;
  const isRefreshing = auth?.isRefreshing;
  const profile = useSelectProfile();
  const workspacesState = useSelectWorkspaces();
  const collaboratorsState = useSelectCollaborators();
  const favoriteList = useSelectFavoriteList();
  const unreadAnnouncementState = useSelectUnreadAnnouncement();
  const announcementList = unreadAnnouncementState?.announcementList;
  // Closed promotions stay closed for this login, including when the workspace page is shown again.
  const showNewFeaturePopup = !!announcementList?.length && !unreadAnnouncementState?.dismissed;
  const settings = useSelectSettings();
  const loadSession = loadSessionStorage();
  const {
    isFavoritesOrSharedWithMe,
    workspaceId: currentWorkspaceId,
    workspace: currentWorkspace,
  } = useCurrentWorkspaceState();
  const { globalWorkspacesList, ...workspaces } = workspacesState;
  const isOwnWorkspace = currentWorkspace?.identityId === profile?.identityId;
  const dexcomIntegrationList = currentWorkspace?.dexcomIntegrationList || [];
  const approvedDexcomIntegrationList =
    dexcomIntegrationList.filter(
      (dexcomIntegration: DexcomIntegration) => dexcomIntegration?.dexcomApp,
    ) || [];
  const appendingDexcomIntegrationList =
    dexcomIntegrationList.filter(
      (dexcomIntegration: DexcomIntegration) => !dexcomIntegration?.dexcomApp,
    ) || [];
  const role = workspacesState?.roleInWorkspaces[currentWorkspaceId ?? ""] || "owner";
  const projectData = useSelectProjectData() as Record<string, IProject>;
  const navigate = useNavigate();
  const collaboratorType = useCollaboratorTypeInProject();
  const canCreateCollaborator = collaboratorTypeHasRole({
    collaboratorType,
    projectRole: ProjectCollaboratorRole.CollaboratorCreate,
    workspaceRole: WorkspaceCollaboratorRole.CollaboratorCreate,
  });
  const isStaffUser = checkIsStaffUser(loadSession?.identity);

  /* ------------------ basic state ------------------ */
  const { accountPlan, hasActivePlanPurchase } = useCurrentWorkspaceAccountPlan();
  const participantLimit = hasActivePlanPurchase ? currentWorkspace?.participantLimit : 5;

  const projectList = useMemo(() => {
    if (!currentWorkspaceId) {
      return [];
    }

    const activeProjectList = Object.values(projectData).filter(
      (item) => item.status !== ProjectStatus.Deleted,
    );

    const result: IProject[] = [];
    if (currentWorkspaceId === "favorites") {
      for (const favorite of favoriteList) {
        for (const project of activeProjectList) {
          if (project.id === favorite.projectId) {
            result.push(project);
            break;
          }
        }
      }
    } else if (currentWorkspaceId === "sharedWithMe") {
      for (const project of activeProjectList) {
        if (
          workspacesState.roleInWorkspaces[project.workspaceId] ===
          WorkspaceCollaboratorType.ProjectCollaborator
        ) {
          result.push(project);
        }
      }
    } else {
      for (const project of activeProjectList) {
        if (project.workspaceId === currentWorkspaceId) {
          result.push(project);
        }
      }
    }

    return result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [currentWorkspaceId, favoriteList, projectData, workspacesState]);

  const [collaboratorList, setCollaboratorList] = useState<IWorkspaceCollaborator[]>([]);
  const templateProjectList = globalWorkspacesList.flatMap(({ projectList }) =>
    projectList.filter((project) => project.status !== ProjectStatus.Deleted),
  );

  useEffect(() => {
    if (!requestResultOfGlobalWorkspaceListFetch?.success) {
      return;
    }
    const projectIds = (
      requestResultOfGlobalWorkspaceListFetch.result.payload.payload as GlobalWorkspace[]
    ).flatMap((workspace) => workspace.projectList.map((project) => project.id));
    if (projectIds.length) {
      doREQUEST_PROJECT_EXTENSION_LIST_FETCH({
        payload: {
          projectIds,
        },
      });
    }
  }, [requestResultOfGlobalWorkspaceListFetch]);

  // display other component
  const [displayCollaboratorsPage, setDisplayCollaboratorsPage] = useState(false);
  const [displayChangePlanPage, setDisplayChangePlanPage] = useState(false);
  const [displayPopupCreateProject, setDisplayPopupCreateProject] = useState(false);

  // display workspace settings
  const [isWorkspaceSettingsPage, setIsWorkspaceSettingsPage] = useState(false);

  // project operation
  const [requestResultOfProjectCreate, setRequestResultOfProjectCreate] =
    useState<RequestResult | null>(null);
  const [requestResultOfProjectDuplicate, setRequestResultOfProjectDuplicate] =
    useState<RequestResult | null>(null);
  const [requestResultOfProjectDelete, setRequestResultOfProjectDelete] =
    useState<ProjectDeleteRequestResult | null>(null);

  const projectDuplicateCompleteRef = useRef<{ onComplete: () => void } | null>(null);
  const projectDeleteCompleteRef = useRef<{ onComplete: () => void } | null>(null);

  useEffect(() => {
    if (!requestResultOfProjectDelete?.success) return;
    const { projectId, prevProjectStatus } = requestResultOfProjectDelete;
    projectDeleteCompleteRef.current?.onComplete();
    doREQUEST_PROJECT_AUDIT_TRAIL_CREATE({
      payload: {
        projectId,
        action: ProjectAuditTrailAction.Update,
        before: { status: prevProjectStatus },
        after: { status: ProjectStatus.Deleted },
      },
    });
  }, [requestResultOfProjectDelete]);

  useEffect(() => {
    if (!requestResultOfProjectDuplicate?.success) return;
    const projectId = requestResultOfProjectDuplicate.result?.payload?.id;
    if (!projectId) return;

    const isTemplateProject = templateProjectList.some(
      (templateProject) => templateProject.id === projectId,
    );
    if (isTemplateProject) {
      return navigate(`/project_setup/${projectId}`);
    } else {
      projectDuplicateCompleteRef.current?.onComplete();
    }
  }, [requestResultOfProjectDuplicate]);

  const { workspace } = useCurrentWorkspaceState();
  const participantsAmount = workspace?.totalParticipants ?? 0;

  useEffect(() => {
    setSessionStorage({ totalParticipants: participantsAmount });
  }, [participantsAmount]);

  const createNewProject = () => {
    const payload: DeepPartial<IProject> = {
      workspaceId: currentWorkspace?.id,
      name: "My Project",
    };
    payload.scheduleConfig = DefaultProjectScheduleConfig;

    doREQUEST_PROJECT_CREATE({
      setRequestResult: setRequestResultOfProjectCreate,
      payload,
    });
  };

  useEffect(() => {
    if (requestResultOfProjectCreate?.success) {
      setDisplayPopupCreateProject(false);
      const projectId = requestResultOfProjectCreate?.result.payload?.id;
      if (projectId) {
        if (!isStaffUser) {
          doREQUEST_PROJECT_AUDIT_TRAIL_CREATE({
            payload: {
              projectId,
              action: ProjectAuditTrailAction.Create,
            },
          });
        }
        return navigate(`/project_setup/${projectId}`);
      }
    }
  }, [requestResultOfProjectCreate]);

  // set collaborator list
  useEffect(() => {
    if (!currentWorkspaceId) return;
    doREQUEST_WORKSPACE_COLLABORATOR_LIST_FETCH({
      setRequestResult: setRequestResultOfCollaboratorListFetch,
      payload: {
        workspaceId: currentWorkspaceId,
      },
    });
    // if (collaboratorsState[currentWorkspaceId]) {
    //   setCollaboratorList(Object.values(collaboratorsState[currentWorkspaceId]).filter((c) => c.profile?.id))
    // }
  }, [currentWorkspaceId]);
  useEffect(() => {
    if (!requestResultOfCollaboratorListFetch?.success) return;
    const { payload } = requestResultOfCollaboratorListFetch.result;
    if (!_.isEqual(payload, collaboratorList)) {
      workspace?.ownerProfile;
      setCollaboratorList([
        {
          identityId: workspace?.ownerProfile.identityId,
          type: "owner" as WorkspaceCollaboratorType,
          workspaceId: currentWorkspaceId,
          workspace,
          profile: workspace?.ownerProfile,
        },
        ...payload.filter((c: IWorkspaceCollaborator) => c.profile?.id),
      ]);
    }
  }, [requestResultOfCollaboratorListFetch]);

  /* ------------------ request ------------------ */

  useEffect(() => {
    if (!currentWorkspaceId) return;
    doREQUEST_PROJECT_LIST_FETCH({
      setRequestResult: setRequestResultOfProjectListFetch,
      payload: {
        page: 0,
      },
    });

    doREQUEST_DEXCOM_INTEGRATION_LIST_FETCH({
      payload: {
        workspaceId: currentWorkspaceId,
      },
    });
  }, [currentWorkspaceId]);

  /* ------------------ default effect ------------------ */

  useMount(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    doREQUEST_GLOBAL_WORKSPACE_LIST_FETCH({
      setRequestResult: setRequestResultOfGlobalWorkspaceListFetch,
    });
  });

  useEffect(() => {
    if (isRefreshing) return;
    if (accessToken && !profile?.id) {
      navigate(`/account_setup/profile_initial_settings_page`);
    }
  }, [accessToken, isRefreshing, profile?.id]);

  const handleWorkspaceFavorite = (project: IProject) => {
    doREQUEST_WORKSPACE_FAVORITE_CREATE({
      payload: {
        projectId: project.id,
      },
    });
  };

  const handleWorkspaceUnfavorite = (project: IProject) => {
    const findResult = _.find(favoriteList, ["projectId", project.id]);
    if (!findResult) return;
    doREQUEST_WORKSPACE_FAVORITE_DELETE({
      payload: {
        workspaceFavoriteId: findResult.id,
      },
    });
  };

  const handleProjectDuplicate = (project: IProject, data: Record<string, string>) => {
    const payload: DeepPartial<IProject> & Record<string, string> = {
      projectId: project.id,
      ...data,
    };
    doREQUEST_PROJECT_DUPLICATE({
      setRequestResult: setRequestResultOfProjectDuplicate,
      payload,
    });
  };

  const handleProjectDelete = (project: IProject) => {
    doREQUEST_PROJECT_STATUS_UPDATE({
      setRequestResult: (result: RequestResult) =>
        setRequestResultOfProjectDelete({
          ...result,
          projectId: project.id,
          prevProjectStatus: project.status,
        }),
      payload: {
        projectId: project.id,
        status: ProjectStatus.Deleted,
        batchId: project.batchList?.at(0)?.id,
      },
    });
  };

  const handleClickSettings = (project: IProject) => {
    navigate(`/settings/${project.id}`);
  };

  const projectCard = (project: IProject, index: number) => {
    const projectId = project.id;
    const projectWorkspace = workspaces[project.workspaceId];
    const isFavorite = favoriteList.some((favorite) => favorite.projectId === projectId);
    return (
      <ProjectCard
        {...{
          key: projectId,
          index,
          project,
          projectWorkspace,
          isFavorite,
          deleteCompletionRef: projectDeleteCompleteRef,
          duplicateCompletionRef: projectDuplicateCompleteRef,
          onClickWorkspaceFavorite: handleWorkspaceFavorite,
          onClickWorkspaceUnfavorite: handleWorkspaceUnfavorite,
          onClickProjectDuplicate: handleProjectDuplicate,
          onClickProjectDelete: handleProjectDelete,
          onClickSettings: handleClickSettings,
        }}
      />
    );
  };

  return (
    <>
      {showNewFeaturePopup && announcementList && (
        <PopupNewFeature
          {...{
            announcementList,
            onClose: () => doUNREAD_ANNOUNCEMENT_DISMISS(),
          }}
        />
      )}
      {RIF(displayCollaboratorsPage, <CollaboratorPage {...{ setDisplayCollaboratorsPage }} />)}
      {RIF(displayChangePlanPage, <PricingPage {...{ setDisplayChangePlanPage }} />)}
      {RIF(
        displayPopupCreateProject,
        <PopupCreateProject
          {...{
            onClickStartFromScratch: createNewProject,
            onClickClose: () => setDisplayPopupCreateProject(false),
          }}
        />,
      )}
      {RIF(
        isWorkspaceSettingsPage,
        <WorkspaceSettings
          {...{
            setIsWorkspaceSettingsPage,
            scrollToDexcom: true,
          }}
        />,
      )}

      {/* page container */}
      <div
        css={{
          minHeight: "100vh",
          background: color.background,
          position: "relative",
          "a:focus-visible, button:focus-visible": {
            outline: `2px solid ${color.primary}`,
            outlineOffset: "2px",
          },
        }}
      >
        <WorkspaceSidebar />
        {/* favorite */}
        {RIF(
          currentWorkspaceId === "favorites",
          <div
            css={{
              padding: "30px 30px 30px 286px",
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              width: "100vw",
            }}
          >
            <h1
              css={{
                fontSize: fontSize.h3,
                fontWeight: fontWeight.thick,
                marginBottom: "16px",
              }}
            >
              Favorites
            </h1>
            {RIF(
              projectList.length,
              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {projectList.map((project, index) => projectCard(project, index))}
              </div>,
            )}
            {RIF(
              !projectList.length,
              <div
                css={{
                  flex: 1,
                  borderRadius: "4px",
                  border: `1px dashed ${color.grey_400}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  css={{ marginBottom: "24px" }}
                  width="244"
                  src={FavoriteEmptyState}
                  alt={"Empty state illustration for favorites section"}
                />
                <p
                  css={{
                    color: color.textIcon.secondary,
                    fontWeight: fontWeight.thick,
                    fontSize: fontSize.h4,
                    marginBottom: "8px",
                  }}
                >
                  No favorite projects yet
                </p>
                <p
                  css={{
                    color: color.textIcon.light,
                    fontSize: fontSize.h5,
                  }}
                >
                  Star projects and they’ll appear here for easy access.
                </p>
              </div>,
            )}
          </div>,
        )}
        {/* shared with me */}
        {RIF(
          currentWorkspaceId === "sharedWithMe",
          <div
            css={{
              padding: "30px 30px 30px 286px",
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              width: "100vw",
            }}
          >
            <h1
              css={{
                fontSize: fontSize.h3,
                fontWeight: fontWeight.thick,
                marginBottom: "16px",
              }}
            >
              Shared with me
            </h1>
            {RIF(
              !projectList.length,
              <div
                css={{
                  flex: 1,
                  borderRadius: "4px",
                  border: `1px dashed ${color.grey_400}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  css={{ marginBottom: "24px" }}
                  width="244"
                  src={SharedWithMeEmptyState}
                  alt={"Empty state illustration for shared projects section"}
                />
                <p
                  css={{
                    color: color.textIcon.secondary,
                    fontWeight: fontWeight.thick,
                    fontSize: fontSize.h4,
                    marginBottom: "8px",
                  }}
                >
                  No shared with me projects
                </p>
                <p
                  css={{
                    color: color.textIcon.light,
                    fontSize: fontSize.h5,
                  }}
                >
                  Projects shared with you by others will be displayed here.
                </p>
              </div>,
            )}
            {RIF(
              projectList.length,
              <div
                css={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {projectList.map((project, index) => projectCard(project, index))}
              </div>,
            )}
          </div>,
        )}
        {/* workspace */}
        {RIF(
          currentWorkspaceId && !isFavoritesOrSharedWithMe,
          <div
            css={{
              padding: "30px 30px 30px 286px",
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              width: "100vw",
            }}
          >
            {/* top state */}
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              {/* workspace name + upgrade button + workspace settings button */}
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <h1
                  css={{
                    fontSize: fontSize.h3,
                    fontWeight: fontWeight.bold,
                    marginRight: "16px",
                  }}
                >
                  {currentWorkspace?.name}
                </h1>
                {/* upgrade button */}
                {RIF(
                  isOwnWorkspace && (accountPlan === "free" || !hasActivePlanPurchase),
                  <Button
                    onClick={() => setDisplayChangePlanPage(true)}
                    btnColor="upgrade"
                    css={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "16px",
                      fontSize: fontSize.h7,
                      fontWeight: fontWeight.medium,
                      padding: "4px 8px 4px 4px",
                    }}
                  >
                    <>
                      <img
                        css={{ marginRight: "4px" }}
                        height="16"
                        width="16"
                        src={ThunderWhiteIcon}
                        alt={"Thunder icon for upgrade button"}
                      />
                      Upgrade
                    </>
                  </Button>,
                )}
                {/* workspace settings button */}
                {RIF(
                  collaboratorType !== WorkspaceCollaboratorType.ProjectCollaborator,
                  <button
                    type="button"
                    onClick={() => setIsWorkspaceSettingsPage(true)}
                    css={{
                      fontSize: fontSize.h7,
                      color: color.textIcon.secondary,
                      border: `1px solid ${color.textIcon.secondary}`,
                      borderRadius: "3px",
                      background: "transparent",
                      cursor: "pointer",
                      padding: "4px 8px",
                      ":hover": {
                        background: color.surface.hover.white,
                      },
                    }}
                  >
                    Workspace Settings
                  </button>,
                )}
              </div>
              {/* create project button */}
              {RIF(
                role !== "viewer",
                <Button
                  btnPadding="medium"
                  onClick={() => {
                    setDisplayPopupCreateProject(true);
                  }}
                >
                  <>
                    <img
                      src={PlusIcon}
                      width="11"
                      css={{ marginRight: "8px" }}
                      alt={"Create New Project"}
                    />
                    Create New Project
                  </>
                </Button>,
              )}
            </div>
            {/* tutorial block */}
            <div
              css={{
                marginBottom: "16px",
                borderRadius: "5px",
                backgroundColor: color.surface.grey.dark,
                padding: "16px",
                width: "100%",
              }}
            >
              <div
                css={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <h2
                  css={{
                    fontWeight: fontWeight.thick,
                  }}
                >
                  Get Started with Labfront
                </h2>
                <p
                  css={{
                    color: color.textIcon.secondary,
                    fontWeight: fontWeight.thick,
                    fontSize: fontSize.h7,
                  }}
                >
                  Learn more: Visit&nbsp;
                  <a
                    href="https://help.labfront.com/"
                    target="_blank"
                    css={{
                      textDecoration: "underline",
                      color: color.primary,
                      fontWeight: fontWeight.thick,
                      fontSize: fontSize.h6,
                    }}
                  >
                    Help Center
                  </a>
                  &nbsp;or&nbsp;
                  <a
                    href="https://help.labfront.com/kb-tickets/new"
                    target="_blank"
                    css={{
                      textDecoration: "underline",
                      color: color.primary,
                      fontWeight: fontWeight.thick,
                      fontSize: fontSize.h6,
                    }}
                  >
                    Schedule a Call
                  </a>
                </p>
              </div>
              <div
                css={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    width: "calc(50% - 8px)",
                    borderRadius: "5px",
                    backgroundColor: color.white,
                    padding: "16px",
                  }}
                >
                  <p
                    aria-label="Number 1"
                    role="img"
                    css={{
                      width: "32px",
                      minWidth: "32px",
                      height: "32px",
                      minHeight: "32px",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "32px",
                      backgroundColor: color.surface.grey.dark,
                      color: color.textIcon.secondary,
                      fontWeight: fontWeight.thick,
                      fontSize: fontSize.h5,
                    }}
                  >
                    1
                  </p>
                  <div css={{ margin: "0 16px" }}>
                    <p
                      css={{
                        fontWeight: fontWeight.thick,
                        marginBottom: "8px",
                      }}
                    >
                      Watch Labfront Walkthrough
                    </p>
                    <p
                      css={{
                        fontSize: fontSize.h7,
                        color: color.grey_600,
                      }}
                    >
                      We’ll guide you through the key steps to get started with Labfront
                    </p>
                  </div>
                  <VideoThumbnail videoId="yAOEdMIC_o4" alt={"Getting started video thumbnail"} />
                </div>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    width: "calc(50% - 8px)",
                    borderRadius: "5px",
                    backgroundColor: color.white,
                    padding: "16px",
                  }}
                >
                  <p
                    aria-label="Number 2"
                    role="img"
                    css={{
                      width: "32px",
                      minWidth: "32px",
                      height: "32px",
                      minHeight: "32px",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "32px",
                      backgroundColor: color.surface.grey.dark,
                      color: color.textIcon.secondary,
                      fontWeight: fontWeight.thick,
                      fontSize: fontSize.h5,
                    }}
                  >
                    2
                  </p>
                  <div css={{ margin: "0 16px" }}>
                    <p
                      css={{
                        fontWeight: fontWeight.thick,
                        marginBottom: "8px",
                      }}
                    >
                      Getting Started
                    </p>
                    <p
                      css={{
                        fontSize: fontSize.h7,
                        marginBottom: "8px",
                        color: color.grey_600,
                      }}
                    >
                      Everything you need to get started
                    </p>
                    <a
                      href="https://help.labfront.com/getting-started"
                      target="_blank"
                      css={{
                        display: "flex",
                        alignItems: "center",
                        color: color.primary,
                        fontWeight: fontWeight.thick,
                        fontSize: fontSize.h7,
                      }}
                    >
                      Check it out
                      <img
                        css={{ marginLeft: "4px" }}
                        width="16"
                        src={LinkToBlueIcon}
                        alt={"Link arrow to getting started resources"}
                      />
                    </a>
                  </div>
                  {/* <VideoThumbnail videoId="UqBiEBV6KTY"/> */}
                  <img
                    width="112"
                    src={GettingStarted}
                    alt={"Image of person with laptop and phone"}
                  />
                </div>
              </div>
            </div>
            {/* project list + workspace info */}
            <div css={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <div
                css={{
                  paddingRight: "15px",
                  borderRight: `1px solid ${color.grey_160}`,
                  fontWeight: fontWeight.medium,
                }}
              >
                Total {projectList?.length} project{projectList?.length === 1 ? "" : "s"}
              </div>
              <div
                css={{
                  borderRight:
                    collaboratorType === WorkspaceCollaboratorType.ProjectCollaborator
                      ? "none"
                      : `1px solid ${color.grey_160}`,
                  paddingRight: "15px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>
                  {participantsAmount} / {participantLimit} participant slots used
                </span>

                <Tooltip
                  css={{ marginLeft: "3px" }}
                  {...{
                    alt: "Learn more about participant slots",
                    position: "up",
                    content:
                      "This is your total participant limit across all projects. Contact us if you'd like to upgrade.",
                  }}
                />
              </div>
              {RIF(
                collaboratorList.length === 0 &&
                  collaboratorType !== WorkspaceCollaboratorType.ProjectCollaborator &&
                  canCreateCollaborator,
                <button
                  type="button"
                  onClick={() => setDisplayCollaboratorsPage(true)}
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor: "transparent",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <img src={CollaborateAddIcon} width="20" alt={"Add collaborator icon"} />
                  <span css={{ color: color.primary, fontWeight: fontWeight.thick }}>
                    Add Collaborator
                  </span>
                </button>,
              )}
              {RIF(
                collaboratorList.length > 0 &&
                  collaboratorType !== WorkspaceCollaboratorType.ProjectCollaborator,
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <p css={{ fontWeight: fontWeight.medium }}>
                    Total {collaboratorList.length} Collaborators
                  </p>
                  <Avatars
                    onClick={() => setDisplayCollaboratorsPage(true)}
                    css={{
                      cursor: "pointer",
                    }}
                    users={collaboratorList}
                  />
                  {RIF(
                    canCreateCollaborator,
                    <ButtonReverse
                      onClick={() => setDisplayCollaboratorsPage(true)}
                      btnPadding="small"
                      btnColor="grey_600"
                      children="Invite"
                    />,
                  )}
                </div>,
              )}
              {RIF(
                dexcomIntegrationList.length,
                <div
                  css={{
                    padding: "0 15px",
                    borderLeft: `1px solid ${color.grey_160}`,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <p
                    css={{
                      fontWeight: fontWeight.medium,
                    }}
                  >
                    Dexcom Integration:
                  </p>
                  {RIF(
                    approvedDexcomIntegrationList.length,
                    <p
                      css={{
                        fontWeight: fontWeight.thick,
                        color: color.task.green,
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "8px",
                      }}
                    >
                      <img
                        src={CheckSuccessIcon}
                        alt={`Icon indicating ${approvedDexcomIntegrationList.length} Dexcom integrations approved`}
                        width={16}
                      />
                      &nbsp;{approvedDexcomIntegrationList.length} Approved
                    </p>,
                  )}
                  {RIF(
                    appendingDexcomIntegrationList.length,
                    <p
                      css={{
                        fontWeight: fontWeight.thick,
                        color: color.warning,
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "8px",
                      }}
                    >
                      <img
                        src={WarningIcon}
                        width={16}
                        alt={`Warning icon indicating ${appendingDexcomIntegrationList.length} Dexcom integrations awaiting approval`}
                      />
                      &nbsp;{appendingDexcomIntegrationList.length} Awaiting approval
                    </p>,
                  )}
                  <div
                    css={{
                      marginLeft: "4px",
                      marginRight: "8px",
                      width: "16px",
                    }}
                  >
                    <Tooltip content="Your application is pending Dexcom approval. Please check your email for updates, or contact us at hello@labfront.com with any questions." />
                  </div>
                  <ButtonReverse
                    onClick={() => {
                      setIsWorkspaceSettingsPage(true);
                    }}
                    btnPadding="small"
                    btnColor="grey_400"
                    children="View"
                  />
                </div>,
              )}
            </div>

            {RIF(
              requestResultOfProjectListFetch && !projectList.length,
              <div
                css={{
                  marginTop: "20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  flex: 1,
                  border: `1px dashed ${color.grey_160}`,
                  borderRadius: "5px",
                }}
              >
                <div
                  css={{
                    fontSize: "18px",
                    color: color.textIcon.secondary,
                  }}
                >
                  No projects yet
                </div>
                <div
                  css={{
                    fontSize: "16px",
                    color: color.textIcon.light,
                    textAlign: "center",
                    marginTop: "8px",
                  }}
                >
                  Create a project to start collecting data and
                  <br /> discover the next big thing in science!
                </div>
                <div
                  css={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "32px",
                  }}
                >
                  <button
                    type="button"
                    onClick={createNewProject}
                    css={{
                      height: "166px",
                      width: "160px",
                      borderRadius: "5px",
                      border: `1px dashed ${color.textIcon.link}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      backgroundColor: "transparent",
                      margin: "0 8px",
                      padding: "0 24px",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      css={{ marginBottom: "8px" }}
                      src={PlusBlueIcon}
                      width="24"
                      alt={"Plus icon"}
                    />
                    <p
                      css={{
                        fontWeight: fontWeight.thick,
                        fontSize: fontSize.h5,
                        color: color.textIcon.link,
                      }}
                    >
                      Start from scratch
                    </p>
                  </button>
                  {_.slice(templateProjectList, templateProjectList.length - 2).map((project) => {
                    return (
                      <TemplateProjectCard
                        {...{
                          key: project.id,
                          project,
                          onClickProjectDuplicate: handleProjectDuplicate,
                        }}
                      />
                    );
                  })}
                </div>
              </div>,
            )}
            {RIF(
              requestResultOfProjectListFetch && projectList.length,
              <>
                <div
                  css={{
                    marginTop: "16px",
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  {projectList.map((project, index) => projectCard(project, index))}
                  {RIF(
                    projectList.length % 3 !== 0,
                    <div css={{ width: "31.8%", height: "220px" }}></div>,
                  )}
                </div>
              </>,
            )}
          </div>,
        )}
      </div>
    </>
  );
};

interface VideoThumbnailProps {
  videoId: string;
  alt: string;
}

const VideoThumbnail = (props: VideoThumbnailProps) => {
  const { videoId, alt } = props;
  const handleClick = () => {
    window.open(`https://www.youtube.com/watch?v=${videoId}`, "_blank");
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={alt}
      css={{
        cursor: "pointer",
        width: "137px",
        height: "72px",
        display: "flex",
        borderRadius: "8px",
        backgroundImage: `url(https://img.youtube.com/vi/${videoId}/0.jpg)`,
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        border: "none",
        padding: 0,
      }}
    >
      <span
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: 1,
          background: "#00000040",
        }}
      >
        <img width="40" src={PlayButtonIcon} alt="" aria-hidden />
      </span>
    </button>
  );
};

interface TemplateProjectCardProps {
  project: IProject;
  onClickProjectDuplicate: (project: IProject, data: Record<string, string>) => void;
}

const TemplateProjectCard = (props: TemplateProjectCardProps) => {
  const { color, fontSize, fontWeight } = useSelectTheme();
  const { project, onClickProjectDuplicate } = props;
  const workspacesState = useSelectWorkspaces();
  const duplicateTemplateProject = () => {
    if (!workspacesState?.currentWorkspaceId) return;
    onClickProjectDuplicate(project, {
      projectName: project.name,
      workspaceId: workspacesState.currentWorkspaceId,
    });
  };

  return (
    <button
      onClick={duplicateTemplateProject}
      css={{
        width: "160px",
        height: "166px",
        padding: "16px",
        backgroundColor: color.white,
        borderRadius: "5px",
        margin: "8px",
        border: "none",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      }}
    >
      <div
        css={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <div
          aria-label="Heart rate icon"
          role="img"
          css={{
            height: "24px",
            width: "24px",
            borderRadius: "4px",
            backgroundColor: color.surface.orange.background,
            backgroundImage: `url(${HeartRateWhiteIcon})`,
            backgroundSize: "20px",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        />
        <p
          css={{
            fontSize: fontSize.h7,
            fontWeight: fontWeight.thick,
            color: color.textIcon.link,
          }}
        >
          Use Template
        </p>
      </div>
      <div
        css={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <p
          css={{
            marginBottom: "8px",
            textAlign: "left",
          }}
        >
          {project.name}
        </p>
        <p
          css={{
            width: "100%",
            color: color.textIcon.secondary,
            textAlign: "left",
            fontSize: fontSize.h7,
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {project.description}
        </p>
      </div>
    </button>
  );
};
